import { useLazyQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { usePuppyMode } from '../../../contexts/PuppyModeContext';
import type {
  SearchSuppliersQuery,
  SearchSuppliersQueryVariables,
  UsersPageSearchSupplierFragmentFragment,
} from '../../../generated';
import { getSelectOptionFromSupplier } from '../components/SupplierLinking/utils';
import { SEARCH_SUPPLIERS_QUERY } from '../data/queries/searchSupplierEntity.gql';

export interface SupplierSelectOption {
  value: string;
  label: string;
}

export interface UseSupplierSearchResponse {
  isLoading: boolean;
  options: SupplierSelectOption[];
  searchSuppliers: (searchTerm: string) => void;
}

export const useSupplierSearch = (): UseSupplierSearchResponse => {
  const [executeSearch, { data, loading }] = useLazyQuery<
    SearchSuppliersQuery,
    SearchSuppliersQueryVariables
  >(SEARCH_SUPPLIERS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const { puppyFilter } = usePuppyMode();

  const searchSuppliers = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        executeSearch({
          variables: {
            keywords: searchTerm,
            limit: 30,
            ...(puppyFilter !== undefined && {
              filter: { isPuppy: puppyFilter },
            }),
          },
        });
      }
    },
    [executeSearch, puppyFilter]
  );

  const options: SupplierSelectOption[] = useMemo(() => {
    if (
      !data?.supplierSearch ||
      data.supplierSearch.__typename !== 'SearchSupplierConnection'
    ) {
      return [];
    }

    return data.supplierSearch.edges
      .filter((edge) => edge.node?.__typename === 'Supplier')
      .map((edge) =>
        getSelectOptionFromSupplier(
          edge.node as UsersPageSearchSupplierFragmentFragment
        )
      );
  }, [data]);

  return {
    isLoading: loading,
    options,
    searchSuppliers,
  };
};
